import type { AuthError } from '../../../services/auth'
import ErrorAlert from '../../ui/ErrorAlert'
import { ERROR_KINDS, T, extractCorrelationId, mapErrorToKind } from './copy'
import { GhostButton, PrimaryButton, ThresholdCanvas, WarningMark } from './primitives'

interface AuthErrorScreenProps {
  error: AuthError
  onRetry: () => void
  onHome?: () => void
}

/** Strips MSAL error params from the URL so a retry starts from a clean slate. */
function clearErrorParams(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete('error')
  url.searchParams.delete('error_description')
  window.history.replaceState({}, '', url.pathname + url.search)
}

/**
 * Screen 4 — the auth-error threshold. A single recoverable treatment; the
 * error kind and correlation id are derived from the AuthContext error, and
 * the headline is announced via aria-live. Technical detail (kind, AADSTS
 * code, correlation id, and the DS error primitive) is gated behind dev mode.
 *
 * @param error - The AuthContext error ({ code, message }).
 * @param onRetry - Invoked after clearing error params (wired to MSAL login()).
 * @param onHome - Optional "Return home" handler; defaults to the public site.
 * @returns The auth-error threshold screen.
 */
export function AuthErrorScreen({ error, onRetry, onHome }: AuthErrorScreenProps) {
  const errorKind = mapErrorToKind(error.code)
  const e = ERROR_KINDS[errorKind]
  const correlationId = extractCorrelationId(error.message)
  const devMode = import.meta.env.DEV

  const handleRetry = () => {
    clearErrorParams()
    onRetry()
  }

  const handleHome = () => {
    if (onHome) {
      onHome()
      return
    }
    window.location.href = '/'
  }

  return (
    <ThresholdCanvas testId="auth-error">
      <div style={{ maxWidth: '30ch' }}>
        <div className="rise" style={{ animationDelay: '260ms' }}>
          <WarningMark size={58} />
        </div>
        <p className="rise" style={{ ...T.eyebrow, marginTop: 26, animationDelay: '420ms' }}>
          {e.eyebrow}
        </p>
        <h1
          className="rise"
          aria-live="polite"
          style={{ ...T.headline, fontSize: 'clamp(1.9rem, 4.4vw, 3.4rem)', marginTop: 16, animationDelay: '560ms' }}
        >
          {e.title}
        </h1>
        <p className="rise" style={{ ...T.body, marginTop: 22, maxWidth: '48ch', animationDelay: '780ms' }}>
          {e.body}
        </p>

        <div
          className="rise"
          style={{ display: 'flex', gap: 14, marginTop: 40, flexWrap: 'wrap', animationDelay: '1000ms' }}
        >
          <PrimaryButton onClick={handleRetry} autoFocus testId="auth-retry-button">
            Try again
          </PrimaryButton>
          <GhostButton onClick={handleHome}>Return home</GhostButton>
        </div>

        {devMode && (
          <details className="fade" style={{ marginTop: 34, maxWidth: 520, animationDelay: '1150ms' }}>
            <summary
              className="at-focusable"
              style={{
                cursor: 'pointer',
                listStyle: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: 'var(--font-mono)',
                fontSize: '.72rem',
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: 'var(--faint)',
                padding: '8px 0',
              }}
            >
              <span style={{ color: 'var(--gold)' }}>›</span>
              Technical detail · developers only
            </summary>
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '.72rem',
                  color: 'var(--muted)',
                  lineHeight: 1.9,
                  marginBottom: 12,
                }}
              >
                <div>
                  <span style={{ color: 'var(--faint)' }}>kind&nbsp;&nbsp;&nbsp;&nbsp;</span> {e.label}
                </div>
                <div>
                  <span style={{ color: 'var(--faint)' }}>code&nbsp;&nbsp;&nbsp;&nbsp;</span> {e.aadsts}
                </div>
                {correlationId && (
                  <div>
                    <span style={{ color: 'var(--faint)' }}>corr-id&nbsp;</span> {correlationId}
                  </div>
                )}
              </div>
              {/* Compose the DS error primitive only where a correlation id exists. */}
              {correlationId && (
                <ErrorAlert
                  tone="dark"
                  message={e.title + ' — ' + e.body}
                  correlationId={correlationId}
                />
              )}
            </div>
          </details>
        )}
      </div>
    </ThresholdCanvas>
  )
}
